import React, { useEffect, useState } from 'react';
import Seo from '../components/Seo';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  adminListLeads, adminListTestimonials, adminApproveTestimonial, adminDeleteTestimonial,
  adminListMeetings, adminUpdateMeetingStatus,
} from '../lib/api';
import { LogOut, Check, X, Trash2, Loader2, Star, ShieldCheck, CalendarClock } from 'lucide-react';
import { company } from '../mock';
import AdminChatTab from './admin/AdminChatTab';
import AdminTeamTab from './admin/AdminTeamTab';
import AdminBlogTab from './admin/AdminBlogTab';
import AdminSubscribersTab from './admin/AdminSubscribersTab';
import AdminStoriesTab from './admin/AdminStoriesTab';
import AdminCampusTab from './admin/AdminCampusTab';

const AdminPage = () => {
  const { toast } = useToast();
  const [token, setToken] = useState(localStorage.getItem('hipro_admin_token') || '');
  const [authed, setAuthed] = useState(!!localStorage.getItem('hipro_admin_token'));
  const [leads, setLeads] = useState([]);
  const [tests, setTests] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [l, t, m] = await Promise.all([adminListLeads(), adminListTestimonials(), adminListMeetings()]);
      setLeads(l); setTests(t); setMeetings(m);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem('hipro_admin_token');
        setAuthed(false);
        toast({ title: 'Session expired', description: 'Please sign in again.' });
      } else {
        toast({ title: 'Could not load data', description: 'Try again in a moment.' });
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (authed) loadAll(); /* eslint-disable-next-line */ }, [authed]);

  const login = (e) => {
    e.preventDefault();
    if (!token) return;
    localStorage.setItem('hipro_admin_token', token);
    setAuthed(true);
  };

  const logout = () => {
    localStorage.removeItem('hipro_admin_token');
    setToken('');
    setAuthed(false);
    setLeads([]); setTests([]); setMeetings([]);
  };

  const setMeetingStatus = async (id, status) => {
    try {
      await adminUpdateMeetingStatus(id, status);
      setMeetings((cur) => cur.map((m) => (m.id === id ? { ...m, status } : m)));
    } catch { toast({ title: 'Update failed' }); }
  };

  const setApproved = async (id, val) => {
    try {
      await adminApproveTestimonial(id, val);
      setTests((cur) => cur.map((t) => (t.id === id ? { ...t, approved: val } : t)));
    } catch { toast({ title: 'Update failed' }); }
  };

  const removeT = async (id) => {
    try {
      await adminDeleteTestimonial(id);
      setTests((cur) => cur.filter((t) => t.id !== id));
    } catch { toast({ title: 'Delete failed' }); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-5">
        <Seo title="Admin | Hipro Technologies" description="Admin console." path="/admin" noindex />
        <form onSubmit={login} className="w-full max-w-md rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-8">
          <div className="flex items-center gap-3">
            <img src={company.logo} alt="logo" className="h-10 w-auto rounded" />
            <div className="font-bold">Hipro Admin</div>
          </div>
          <h1 className="mt-6 text-2xl font-extrabold">Sign in</h1>
          <p className="mt-1 text-sm text-slate-300">Enter your admin token to manage leads and testimonials.</p>
          <div className="mt-6">
            <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Admin token" className="h-11 text-slate-900" />
          </div>
          <Button type="submit" className="mt-4 w-full h-11 bg-orange-500 hover:bg-orange-600"><ShieldCheck className="h-4 w-4 mr-2" /> Access dashboard</Button>
          <p className="mt-3 text-xs text-slate-400">Token is stored only in your browser.</p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={company.logo} alt="logo" className="h-8 w-auto rounded" />
            <div className="font-bold">Hipro Admin</div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={loadAll} className="bg-transparent border-white/20 text-white hover:bg-white/10">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
            </Button>
            <Button onClick={logout} className="bg-orange-500 hover:bg-orange-600"><LogOut className="h-4 w-4 mr-2" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <Tabs defaultValue="leads">
          <TabsList>
            <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
            <TabsTrigger value="meetings">Meetings ({meetings.length})</TabsTrigger>
            <TabsTrigger value="testimonials">Testimonials ({tests.length})</TabsTrigger>
            <TabsTrigger value="chats">Chat Inbox</TabsTrigger>
            <TabsTrigger value="team">Team</TabsTrigger>
            <TabsTrigger value="blog" data-testid="admin-blog-tab">Blog</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="admin-subscribers-tab">Subscribers</TabsTrigger>
            <TabsTrigger value="stories" data-testid="admin-stories-tab">Stories</TabsTrigger>
            <TabsTrigger value="campus" data-testid="admin-campus-tab">Campus Drives</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-6">
            <div className="rounded-2xl border bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="text-left px-4 py-3">Created</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Mobile</th>
                    <th className="text-left px-4 py-3">Service</th>
                    <th className="text-left px-4 py-3">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No leads yet.</td></tr>
                  )}
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t">
                      <td className="px-4 py-3 text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{l.name}</td>
                      <td className="px-4 py-3"><a href={`mailto:${l.email}`} className="text-orange-600 hover:underline">{l.email}</a></td>
                      <td className="px-4 py-3">{l.dial} {l.mobile}</td>
                      <td className="px-4 py-3">{l.service || '—'}</td>
                      <td className="px-4 py-3">{l.hear || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="meetings" className="mt-6">
            <div className="rounded-2xl border bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="text-left px-4 py-3">Booked at</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Slot</th>
                    <th className="text-left px-4 py-3">Service</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">No meetings booked yet.</td></tr>
                  )}
                  {meetings.map((m) => (
                    <tr key={m.id} className="border-t">
                      <td className="px-4 py-3 text-slate-500">{new Date(m.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{m.name}</td>
                      <td className="px-4 py-3"><a href={`mailto:${m.email}`} className="text-orange-600 hover:underline">{m.email}</a></td>
                      <td className="px-4 py-3">{m.date}</td>
                      <td className="px-4 py-3"><span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5 text-orange-500" />{m.slot} ({m.timezone})</span></td>
                      <td className="px-4 py-3">{m.service || '—'}</td>
                      <td className="px-4 py-3">
                        <Badge className={m.status === 'confirmed' ? 'bg-emerald-500' : m.status === 'completed' ? 'bg-slate-600' : 'bg-red-500'}>
                          {m.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 space-x-1">
                        {m.status !== 'completed' && (
                          <Button onClick={() => setMeetingStatus(m.id, 'completed')} variant="outline" className="h-8 px-2 text-xs">Mark done</Button>
                        )}
                        {m.status !== 'cancelled' && (
                          <Button onClick={() => setMeetingStatus(m.id, 'cancelled')} variant="outline" className="h-8 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50">Cancel</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="testimonials" className="mt-6">
            <div className="grid md:grid-cols-2 gap-4">
              {tests.length === 0 && <div className="text-slate-500 col-span-2 py-8 text-center">No testimonials yet.</div>}
              {tests.map((t) => (
                <div key={t.id} className="rounded-2xl border bg-white p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">{t.name}</div>
                      <div className="text-xs text-slate-500">{[t.role, t.company, t.country].filter(Boolean).join(', ')}</div>
                    </div>
                    <Badge className={t.approved ? 'bg-emerald-500' : 'bg-amber-500'}>
                      {t.approved ? 'Approved' : 'Pending'}
                    </Badge>
                  </div>
                  <p className="mt-3 text-sm text-slate-700">“{t.quote}”</p>
                  <div className="mt-2 flex items-center gap-1 text-amber-400">
                    {[...Array(t.rating || 5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                  </div>
                  <div className="mt-4 flex gap-2">
                    {t.approved ? (
                      <Button onClick={() => setApproved(t.id, false)} variant="outline" className="h-9"><X className="h-4 w-4 mr-1" /> Unapprove</Button>
                    ) : (
                      <Button onClick={() => setApproved(t.id, true)} className="h-9 bg-emerald-500 hover:bg-emerald-600 text-white"><Check className="h-4 w-4 mr-1" /> Approve</Button>
                    )}
                    <Button onClick={() => removeT(t.id)} variant="outline" className="h-9 text-red-600 hover:bg-red-50 border-red-200"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="chats" className="mt-6">
            <AdminChatTab />
          </TabsContent>

          <TabsContent value="team" className="mt-6">
            <AdminTeamTab />
          </TabsContent>
          <TabsContent value="blog" className="mt-6">
            <AdminBlogTab />
          </TabsContent>
          <TabsContent value="subscribers" className="mt-6">
            <AdminSubscribersTab />
          </TabsContent>
          <TabsContent value="stories" className="mt-6">
            <AdminStoriesTab />
          </TabsContent>
          <TabsContent value="campus" className="mt-6">
            <AdminCampusTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;
